import React, { useState, useEffect } from 'react'
import { TrendingUp, ShoppingCart, Package, Users, Activity, CalendarRange } from 'lucide-react'
import { CustomerInsightsWidget } from '@/components/dashboard/CustomerInsightsWidget'
import { OrderStatusWidget } from '@/components/dashboard/OrderStatusWidget'
import { PaymentStatsWidget } from '@/components/dashboard/PaymentStatsWidget'
import { StatsCard } from '@/components/dashboard/StatsCard'
import { SalesChart } from '@/components/dashboard/SalesChart'
import { TopProductsWidget } from '@/components/dashboard/TopProductsWidget'
import { RecentOrders } from '@/components/dashboard/RecentOrders'
import { ActivityFeed } from '@/components/dashboard/ActivityFeed'
import { formatIDR } from '@/lib/utils'
import apiClient from '@/lib/apiClient'
import type { DashboardStats } from '@/types/api'

const DashboardPage: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchDashboardStats = async () => {
      try {
        setLoading(true)
        setError(null)
        const response = await apiClient.getDashboardStats({ period: 'all' })
        setStats(response.data)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch dashboard stats')
      } finally {
        setLoading(false)
      }
    }

    fetchDashboardStats()
  }, [])

  if (loading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-600">Loading dashboard data...</p>
        </div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-32 bg-gray-100 animate-pulse rounded-lg" />
          ))}
        </div>
      </div>
    )
  }

  if (error || !stats) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-red-600">
            {error || 'Failed to load dashboard data. Please try again.'}
          </p>
        </div>
      </div>
    )
  }

  const showComparisons = stats.period_days !== 'all'
  const generatedAtLabel = stats.generated_at
    ? new Intl.DateTimeFormat('id-ID', {
        dateStyle: 'medium',
        timeStyle: 'short',
      }).format(new Date(stats.generated_at))
    : null

  return (
    <div className="space-y-8">
      <section className="rounded-[28px] border border-slate-200/80 bg-gradient-to-br from-white via-slate-50 to-blue-50/50 p-6 shadow-sm sm:p-8">
        <div className="flex flex-col gap-6 xl:flex-row xl:items-end xl:justify-between">
          <div className="space-y-4">
            <div className="inline-flex items-center rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-blue-700">
              Operations Dashboard
            </div>
            <div className="space-y-2">
              <h1 className="text-3xl font-semibold tracking-tight text-slate-950 sm:text-4xl">
                Dashboard
              </h1>
              <p className="max-w-2xl text-sm leading-6 text-slate-600 sm:text-base">
                {stats.period_days === 'all'
                  ? 'A clean all-time view of ATP performance, revenue, order volume, and operational activity.'
                  : 'Welcome to the ASG Admin Dashboard'}
              </p>
            </div>
          </div>

          <div className="flex flex-wrap gap-3">
            <div className="rounded-2xl border border-slate-200/80 bg-white/80 px-4 py-3 shadow-sm backdrop-blur">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                <CalendarRange className="h-4 w-4" />
                Snapshot
              </div>
              <p className="mt-1 text-sm font-medium text-slate-900">
                {stats.period_days === 'all' ? 'All-time' : `${stats.period_days} day window`}
              </p>
            </div>
            <div className="rounded-2xl border border-slate-200/80 bg-white/80 px-4 py-3 shadow-sm backdrop-blur">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                <Activity className="h-4 w-4" />
                Avg. Order Value
              </div>
              <p className="mt-1 text-sm font-medium text-slate-900">
                {formatIDR(stats.average_order_value)}
              </p>
            </div>
            {generatedAtLabel && (
              <div className="rounded-2xl border border-slate-200/80 bg-white/80 px-4 py-3 shadow-sm backdrop-blur">
                <div className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                  Updated
                </div>
                <p className="mt-1 text-sm font-medium text-slate-900">{generatedAtLabel}</p>
              </div>
            )}
          </div>
        </div>
      </section>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <StatsCard
          title="Total Revenue"
          description="Net non-cancelled order revenue"
          value={formatIDR(stats.total_revenue)}
          change={showComparisons ? (stats.revenue_change ?? undefined) : undefined}
          changeType={(stats.revenue_change ?? 0) >= 0 ? 'increase' : 'decrease'}
          icon={<TrendingUp className="h-5 w-5" />}
          iconContainerClassName="border-emerald-200 bg-emerald-50 text-emerald-700"
          footerLabel={showComparisons ? 'vs previous period' : 'Across all recorded orders'}
        />
        <StatsCard
          title="Total Orders"
          description="All submitted orders in the system"
          value={stats.total_orders.toLocaleString()}
          change={showComparisons ? (stats.orders_change ?? undefined) : undefined}
          changeType={(stats.orders_change ?? 0) >= 0 ? 'increase' : 'decrease'}
          icon={<ShoppingCart className="h-5 w-5" />}
          iconContainerClassName="border-blue-200 bg-blue-50 text-blue-700"
          footerLabel={showComparisons ? 'vs previous period' : 'All-time order volume'}
        />
        <StatsCard
          title="Active Products"
          description="Products currently visible for operations"
          value={stats.active_products.toLocaleString()}
          icon={<Package className="h-5 w-5" />}
          iconContainerClassName="border-violet-200 bg-violet-50 text-violet-700"
          footerLabel="Live catalog inventory footprint"
        />
        <StatsCard
          title="Total Customers"
          description="Registered customer accounts"
          value={stats.total_customers.toLocaleString()}
          change={showComparisons ? (stats.customers_change ?? undefined) : undefined}
          changeType={(stats.customers_change ?? 0) >= 0 ? 'increase' : 'decrease'}
          icon={<Users className="h-5 w-5" />}
          iconContainerClassName="border-amber-200 bg-amber-50 text-amber-700"
          footerLabel={showComparisons ? 'vs previous period' : 'Customer base to date'}
        />
      </div>

      <div className="grid gap-6 xl:grid-cols-12">
        <OrderStatusWidget className="xl:col-span-4" />
        <CustomerInsightsWidget className="xl:col-span-4" />
        <PaymentStatsWidget className="xl:col-span-4" />
        <SalesChart className="xl:col-span-7" />
        <RecentOrders className="xl:col-span-5" />
        <TopProductsWidget className="xl:col-span-6" />
        <ActivityFeed className="xl:col-span-6" />
      </div>
    </div>
  )
}

export default DashboardPage
